/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { InjectedRouter } from 'react-router';

import { compose } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import imgix from 'js/lib/imgix';

import { Grid, PageGridContainer, Typography, breakpoints } from '@coursera/cds-core';

import { domainBackgroundScoreMap } from 'bundles/browse-common/constants';
import type { Subdomain } from 'bundles/browse-common/legacy/components/Domain/types/SubDomain';
import BrowseBreadcrumbs from 'bundles/browse/components/Domain/BrowseBreadcrumbs';
import DescriptionBubble from 'bundles/browse/components/Domain/DescriptionBubble';
import SubdomainCarousel from 'bundles/browse/components/Domain/SubdomainCarousel';
import type { Domain } from 'bundles/browse/components/Domain/types/Domain';
import { BANNER_HEIGHT } from 'bundles/browse/constants';

const styles = {
  domainBanner: css`
    display: block;
    padding: 32px 0 12px;
    position: relative;
    ${breakpoints.down('md')} {
      max-height: 400px;
    }
    ${breakpoints.down('xs')} {
      padding: 16px 0 0;
    }

    .domain-banner-mask {
      background-color: var(--cds-color-grey-600);
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    .rc-BrowseBreadcrumbs {
      /* to put this component above mask */
      position: relative;
    }

    .subdomain-pill-wrapper {
      width: 100%;

      .title-wrapper {
        margin-top: 12px;
      }
    }

    .topic-image {
      position: absolute;
      top: 0;
      left: 0;
    }

    .domain-name {
      color: var(--cds-color-neutral-primary-invert);
      padding: 12px 0;
      position: relative;
      display: inline;
      ${breakpoints.down('md')} {
        padding: 0;
      }
    }

    .rc-SubdomainCarousel {
      width: 100%;
    }

    .rc-BrowseCarousel {
      .slick-list,
      .slick-track {
        min-width: 100%;
      }
    }
  `,
};

type PropsFromCaller = {
  domain: Domain;
  /** A callback function for generating the domain page url. */
  onGetSubdomainPageUrl?: (domain: Domain, subdomain: Subdomain) => string;
  /** Custom domain card click handler */
  onClick?: (url: string, domain: Domain, subdomain: Subdomain) => void;
};
type PropsFromRouter = { router: InjectedRouter };
type PropsToComponent = PropsFromCaller & PropsFromRouter;

export const DomainBanner = (props: PropsToComponent) => {
  const { domain, router, onGetSubdomainPageUrl, onClick } = props;
  const subdomains = domain.subdomains.elements;
  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const imageUrl = imgix.processImage(domainBackgroundScoreMap[domain.id].bannerUrl, {
    auto: 'compress',
    dpr: 0.4,
    quality: 10,
    blur: 5,
  });

  const getSubdomainPageUrl = (subdomain: Subdomain) => {
    if (onGetSubdomainPageUrl) {
      return onGetSubdomainPageUrl(domain, subdomain);
    } else {
      return `/browse/${domain.slug || domain.id}/${subdomain.id}`;
    }
  };

  const handleClick = (subdomain: Subdomain) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    const url = getSubdomainPageUrl(subdomain);

    event.preventDefault();

    if (onClick) {
      onClick(url, domain, subdomain);
    } else {
      router.push(url);
    }
  };

  const renderDomainBannerContent = () => (
    <PageGridContainer maxWidth="lg">
      <div className="domain-banner-mask" />
      <Grid container component="header">
        <Grid item className="subdomain-pill-wrapper">
          <BrowseBreadcrumbs domain={domain} />
          <div className="title-wrapper">
            <DescriptionBubble
              title={domain.name}
              description={domain.description}
              domainId={domain.id}
              label={
                <Typography variant="d2" component="h1" className="domain-name" data-testid="domain-name">
                  {domain.name}
                </Typography>
              }
            />
          </div>
          {subdomains?.length > 1 && (
            <SubdomainCarousel
              subdomains={subdomains}
              domain={domain}
              handleSubdomainClick={handleClick}
              onGetSubdomainPageUrl={onGetSubdomainPageUrl}
            />
          )}
        </Grid>
      </Grid>
    </PageGridContainer>
  );

  return (
    <div
      className="rc-DomainBanner"
      style={{ backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover', minHeight: BANNER_HEIGHT }}
      data-testid="domain-banner"
      css={styles.domainBanner}
    >
      {renderDomainBannerContent()}
    </div>
  );
};

export default compose<PropsToComponent, PropsFromCaller>(connectToRouter<PropsFromRouter>((router) => ({ router })))(
  DomainBanner
);
